import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  Play,
  Pause,
  Trophy,
  Users,
  Ticket,
  AlertCircle,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  Eye,
  RefreshCw,
  FileText,
  ChevronDown,
  Home,
  Hash,
  Download,
} from 'lucide-react';
import {
  Card,
  Button,
  ShimmerButton,
  Badge,
  Select,
  cn,
  formatNaira,
  StatCard,
  useToast,
} from '@raffle/ui';

interface Draw {
  id: string;
  property: {
    id: string;
    title: string;
    image: string;
    marketValue: number;
  };
  scheduledDate: Date;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  ticketsSold: number;
  totalTickets: number;
  winner?: {
    id: string;
    name: string;
    ticketNumber: string;
  };
  auditLog?: {
    timestamp: Date;
    action: string;
    details: string;
  }[];
}

const mockDraws: Draw[] = [
  {
    id: 'DRW-001',
    property: {
      id: 'p1',
      title: 'Luxury Villa in Lekki Phase 1',
      image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400',
      marketValue: 450000000,
    },
    scheduledDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7),
    status: 'scheduled',
    ticketsSold: 8456,
    totalTickets: 10000,
  },
  {
    id: 'DRW-002',
    property: {
      id: 'p2',
      title: 'Modern Apartment in Victoria Island',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400',
      marketValue: 180000000,
    },
    scheduledDate: new Date(Date.now() + 1000 * 60 * 30),
    status: 'in_progress',
    ticketsSold: 5000,
    totalTickets: 5000,
  },
  {
    id: 'DRW-003',
    property: {
      id: 'p3',
      title: 'Penthouse in Ikoyi',
      image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400',
      marketValue: 650000000,
    },
    scheduledDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
    status: 'completed',
    ticketsSold: 15000,
    totalTickets: 15000,
    winner: {
      id: 'u123',
      name: 'Adebayo Johnson',
      ticketNumber: '08934',
    },
    auditLog: [
      { timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), action: 'draw_started', details: 'Automated draw initiated' },
      { timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2 + 5000), action: 'random_seed', details: 'Cryptographic seed: 0x7f3a...' },
      { timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2 + 10000), action: 'winner_selected', details: 'Ticket #08934 selected' },
      { timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2 + 15000), action: 'draw_completed', details: 'Winner verified and notified' },
    ],
  },
  {
    id: 'DRW-004',
    property: {
      id: 'p4',
      title: 'Beach House in Oniru',
      image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400',
      marketValue: 320000000,
    },
    scheduledDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30),
    status: 'cancelled',
    ticketsSold: 1234,
    totalTickets: 8000,
  },
];

const statusConfig = {
  scheduled: { color: 'warning', icon: Calendar, label: 'Scheduled' },
  in_progress: { color: 'info', icon: RefreshCw, label: 'In Progress' },
  completed: { color: 'success', icon: CheckCircle, label: 'Completed' },
  cancelled: { color: 'error', icon: XCircle, label: 'Cancelled' },
} as const;

export const DrawsPage: React.FC = () => {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDraw, setSelectedDraw] = useState<Draw | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);

  const filteredDraws = mockDraws.filter(
    (draw) => statusFilter === 'all' || draw.status === statusFilter
  );

  const triggerDraw = async (draw: Draw) => {
    setIsTriggering(true);
    // Simulate draw process
    await new Promise((resolve) => setTimeout(resolve, 3000));
    toast.success('Draw Completed', `Winner selected for ${draw.property.title}`);
    setIsTriggering(false);
  };

  const formatCountdown = (date: Date) => {
    const diff = date.getTime() - Date.now();
    if (diff <= 0) return 'Now';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Stats
  const scheduledCount = mockDraws.filter((d) => d.status === 'scheduled').length;
  const completedCount = mockDraws.filter((d) => d.status === 'completed').length;
  const totalPrizeValue = mockDraws
    .filter((d) => d.status === 'completed')
    .reduce((sum, d) => sum + d.property.marketValue, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">Draw Management</h1>
          <p className="text-text-secondary">Schedule, monitor, and trigger property draws</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Upcoming Draws"
          value={scheduledCount}
          icon={<Calendar className="w-5 h-5" />}
        />
        <StatCard
          title="Completed Draws"
          value={completedCount}
          icon={<Trophy className="w-5 h-5" />}
        />
        <StatCard
          title="Total Prize Value"
          value={formatNaira(totalPrizeValue)}
          icon={<Home className="w-5 h-5" />}
        />
        <StatCard
          title="Winners Awarded"
          value={completedCount}
          icon={<Users className="w-5 h-5" />}
        />
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: 'all', label: 'All Draws' },
            { value: 'scheduled', label: 'Scheduled' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'completed', label: 'Completed' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
          className="w-48"
        />
      </div>

      {/* Draws Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {filteredDraws.map((draw) => {
          const config = statusConfig[draw.status];
          const StatusIcon = config.icon;
          const progress = (draw.ticketsSold / draw.totalTickets) * 100;

          return (
            <Card key={draw.id} className="overflow-hidden">
              {/* Header Image */}
              <div className="relative h-40">
                <img
                  src={draw.property.image}
                  alt={draw.property.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute top-4 right-4">
                  <Badge variant={config.color as any}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {config.label}
                  </Badge>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="font-mono text-xs text-text-muted mb-1">{draw.id}</p>
                  <h3 className="font-display text-lg font-semibold text-white line-clamp-1">
                    {draw.property.title}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-text-muted mb-1">Property Value</p>
                    <p className="font-mono text-sm font-medium text-gold-500">
                      {formatNaira(draw.property.marketValue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted mb-1">Tickets Sold</p>
                    <p className="text-sm font-medium text-text-primary">
                      {draw.ticketsSold.toLocaleString()} / {draw.totalTickets.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted mb-1">
                      {draw.status === 'scheduled' ? 'Draw In' : 'Draw Date'}
                    </p>
                    <p className="text-sm font-medium text-text-primary">
                      {draw.status === 'scheduled'
                        ? formatCountdown(draw.scheduledDate)
                        : draw.scheduledDate.toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Progress */}
                <div>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-text-muted">Ticket Sales Progress</span>
                    <span className="text-gold-500 font-medium">{progress.toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-surface-3 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 1 }}
                      className="h-full bg-gradient-to-r from-gold-600 to-gold-400 rounded-full"
                    />
                  </div>
                </div>

                {/* Winner (if completed) */}
                {draw.winner && (
                  <div className="p-4 rounded-vault bg-vault-success/10 border border-vault-success/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-vault-success/20 flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-vault-success" />
                      </div>
                      <div>
                        <p className="text-xs text-vault-success">Winner</p>
                        <p className="text-sm font-medium text-text-primary">{draw.winner.name}</p>
                        <p className="text-xs text-text-muted font-mono">Ticket #{draw.winner.ticketNumber}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  {draw.status === 'scheduled' && progress >= 100 && (
                    <ShimmerButton
                      onClick={() => triggerDraw(draw)}
                      loading={isTriggering}
                      className="flex-1"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Trigger Draw
                    </ShimmerButton>
                  )}
                  {draw.status === 'in_progress' && (
                    <Button variant="outline" className="flex-1" disabled>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Drawing...
                    </Button>
                  )}
                  {draw.status === 'completed' && draw.auditLog && (
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setSelectedDraw(draw);
                        setShowAuditLog(true);
                      }}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      View Audit Log
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => setSelectedDraw(draw)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Audit Log Modal */}
      <AnimatePresence>
        {showAuditLog && selectedDraw?.auditLog && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setShowAuditLog(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-lg bg-surface-1 rounded-vault-lg border border-white/10 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-white/10">
                <h2 className="font-display text-lg font-semibold text-text-primary">
                  Draw Audit Log
                </h2>
                <p className="text-sm text-text-muted">
                  {selectedDraw.property.title} - {selectedDraw.id}
                </p>
              </div>
              <div className="p-6 max-h-96 overflow-y-auto">
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-4 top-2 bottom-2 w-px bg-white/10" />
                  
                  <div className="space-y-6">
                    {selectedDraw.auditLog.map((log, index) => (
                      <div key={index} className="flex gap-4 relative">
                        <div className="w-8 h-8 rounded-full bg-surface-3 border-2 border-gold-500/50 flex items-center justify-center z-10">
                          <CheckCircle className="w-4 h-4 text-gold-500" />
                        </div>
                        <div className="flex-1 pt-1">
                          <p className="text-sm font-medium text-text-primary capitalize">
                            {log.action.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-text-muted mt-0.5">{log.details}</p>
                          <p className="text-xs text-text-muted/60 mt-1">
                            {log.timestamp.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-white/10 flex gap-3">
                <Button variant="outline" className="flex-1">
                  <Download className="w-4 h-4 mr-2" />
                  Export Log
                </Button>
                <Button variant="ghost" onClick={() => setShowAuditLog(false)}>
                  Close
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
